import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RotateCcw, Undo2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-color.png`;
const LOGO_WHITE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/static/kpl-logo-white.png`;
const WICKET_TYPES = [["caught", "Caught"], ["bowled", "Bowled"], ["lbw", "LBW"], ["run_out", "Run out"], ["stumped", "Stumped"], ["hit_wicket", "Hit wicket"], ["caught_and_bowled", "Caught & bowled"], ["obstructing", "Obstructing"], ["retired_out", "Retired out"], ["other", "Other"]];
const fmtOvers = (balls) => `${Math.floor(balls / 6)}.${balls % 6}`;

export default function Scorer() {
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const matchId = params.get("match") || "";
  const [matches, setMatches] = useState(null);
  const [match, setMatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [setup, setSetup] = useState({ overs_limit: 20, toss_winner: "", toss_decision: "bat", squad_a_xi: "", squad_a_impact: "", squad_a_bench: "", squad_b_xi: "", squad_b_impact: "", squad_b_bench: "" });
  const [teamSquads, setTeamSquads] = useState({ a: [], b: [] });
  const [startInn, setStartInn] = useState({ striker: "", non_striker: "", bowler: "" });
  const [extraMode, setExtraMode] = useState("");
  const [wicketMode, setWicketMode] = useState(false);
  const [wicket, setWicket] = useState({ type: "caught", player_out: "", fielder: "" });
  const [note, setNote] = useState("");
  const [bowlerPick, setBowlerPick] = useState("");

  useEffect(() => {
    if (user && (user.role === "admin" || user.role === "scorer")) axios.get(`${API}/matches`).then((r) => setMatches(r.data.matches || [])).catch(() => setMatches([]));
  }, [user]);

  const loadDetail = async (id) => {
    if (!id) return;
    try {
      const { data } = await axios.get(`${API}/matches/${id}`);
      setMatch(data.match);
      const sa = data.match.squads?.a || {};
      const sb = data.match.squads?.b || {};
      const join = (g) => (Array.isArray(g) ? g : g || []).join("\n");
      axios.get(`${API}/teams`).then((r) => {
        const all = r.data.teams || [];
        setTeamSquads({
          a: (all.find((t) => t.name === data.match.team_a)?.squad || []).map((p) => p.name),
          b: (all.find((t) => t.name === data.match.team_b)?.squad || []).map((p) => p.name),
        });
      }).catch(() => {});
      setSetup((s) => ({
        overs_limit: data.match.overs_limit || 20,
        toss_winner: data.match.toss?.winner || s.toss_winner,
        toss_decision: data.match.toss?.decision || "bat",
        squad_a_xi: Array.isArray(sa) ? sa.join("\n") : join(sa.xi) || s.squad_a_xi,
        squad_a_impact: join(sa.impact),
        squad_a_bench: join(sa.bench),
        squad_b_xi: Array.isArray(sb) ? sb.join("\n") : join(sb.xi) || s.squad_b_xi,
        squad_b_impact: join(sb.impact),
        squad_b_bench: join(sb.bench),
      }));
    } catch { setMatch(null); }
  };

  useEffect(() => {
    loadDetail(matchId);
    const timer = setInterval(() => loadDetail(matchId), 12000);
    return () => clearInterval(timer);
  }, [matchId]);

  if (user === null) return <div className="admin-loading">Loading…</div>;
  if (!user || (user.role !== "admin" && user.role !== "scorer")) return <div className="admin-loading">Scorer access requires a KPL scorer or admin account. <Link to="/login">Log in</Link></div>;

  const visibleMatches = (matches || []).filter((m) => user.role === "admin" || (m.scorers || []).includes(user.id));

  const comp = match?.computed;
  const inningsIdx = match?.current_innings || 0;
  const compInn = comp ? (inningsIdx === 0 ? comp.inn1 : comp.inn2) : null;
  const playing = match?.playing;
  const squadPool = (side) => { const s = match?.squads?.[side]; if (!s) return []; return Array.isArray(s) ? s : [...(s.xi || []), ...(s.impact || [])]; };
  const battingSquad = match ? squadPool(compInn?.team === match.team_a ? "a" : "b") : [];
  const bowlingSquad = match ? squadPool(compInn?.team === match.team_a ? "b" : "a") : [];
  const hasSquads = Boolean(match?.squads?.a && match?.squads?.b && (Array.isArray(match.squads.a) ? match.squads.a.length : (match.squads.a.xi || []).length) && (Array.isArray(match.squads.b) ? match.squads.b.length : (match.squads.b.xi || []).length));

  const doAction = async (fn) => {
    setBusy(true);
    setError("");
    try { await fn(); await loadDetail(matchId); } catch (err) { setError(err.response?.data?.detail || "Action failed — try again."); }
    setBusy(false);
  };

  const recordBall = (runs, extra = "", extraRuns = 0) => doAction(async () => {
    const body = { client_id: crypto.randomUUID(), runs, extra, extra_runs: extraRuns, note };
    if (wicketMode) body.wicket = { ...wicket, player_out: wicket.player_out || playing.striker };
    await axios.post(`${API}/admin/matches/${matchId}/ball`, body);
    setNote("");
    setExtraMode("");
    setWicketMode(false);
    setWicket({ type: "caught", player_out: "", fielder: "" });
  });

  const saveSetup = () => doAction(async () => {
    const lines = (v) => v.split("\n").map((x) => x.trim()).filter(Boolean);
    await axios.put(`${API}/admin/matches/${matchId}/setup`, {
      overs_limit: Number(setup.overs_limit) || 20,
      toss_winner: setup.toss_winner,
      toss_decision: setup.toss_decision,
      squad_a: { xi: lines(setup.squad_a_xi), impact: lines(setup.squad_a_impact), bench: lines(setup.squad_a_bench) },
      squad_b: { xi: lines(setup.squad_b_xi), impact: lines(setup.squad_b_impact), bench: lines(setup.squad_b_bench) },
    });
  });

  const startInnings = () => doAction(async () => {
    await axios.post(`${API}/admin/matches/${matchId}/start-innings`, startInn);
    setStartInn({ striker: "", non_striker: "", bowler: "" });
  });

  const undo = () => doAction(async () => { await axios.post(`${API}/admin/matches/${matchId}/undo`); });
  const setBowler = () => doAction(async () => { await axios.put(`${API}/admin/matches/${matchId}/playing`, { bowler: bowlerPick }); setBowlerPick(""); });

  return <div className="scorer-wrap">
    <div className="scorer-top">
      <Link to="/admin" className="auth-home" data-testid="scorer-back"><ArrowLeft size={15} /> DASHBOARD</Link>
      <img src={LOGO_WHITE_URL} alt="KPL" className="scorer-logo" />
      <Link to="/live" className="auth-home" data-testid="scorer-view-live">VIEW PUBLIC</Link>
    </div>

    {!matchId ? <div className="scorer-panel" data-testid="scorer-match-list">
      <h2>Select match to score</h2>
      {visibleMatches.map((m) => <button key={m.id} className="scorer-match-pick" onClick={() => setParams({ match: m.id })} data-testid={`scorer-pick-${m.id}`}>
        <b>{m.team_a} vs {m.team_b}</b><span>{m.status.toUpperCase()} · {m.venue} · {m.date}</span>
      </button>)}
      {matches && visibleMatches.length === 0 && <p>{user.role === "scorer" ? "No matches are assigned to you yet — the KPL admin will assign matches from the dashboard." : "No matches yet — create one in the dashboard Scores tab."}</p>}
    </div> : !match ? <div className="scorer-panel">Loading match…</div> : <>
      <div className="scorer-scorebar" data-testid="scorer-scorebar">
        <b>{compInn?.team}</b>
        <span className="scorer-big">{compInn?.runs}/{compInn?.wickets}</span>
        <span>{compInn?.overs} ov{comp?.target ? ` · need ${comp.need} off ${comp.balls_left}` : ""}</span>
      </div>

      {match.status === "completed" ? <div className="scorer-panel" data-testid="scorer-done">
        <h2>Match completed</h2>
        <p className="scorer-result">{match.result}</p>
        <Link className="button button-red scorer-btn" to="/live" data-testid="scorer-see-public">See public scorecard</Link>
      </div> : !hasSquads ? <div className="scorer-panel" data-testid="scorer-setup">
        <h2>Match setup</h2>
        <div className="field"><label>Overs per innings</label><input type="number" min="1" max="50" value={setup.overs_limit} onChange={(e) => setSetup({ ...setup, overs_limit: e.target.value })} data-testid="setup-overs" /></div>
        <div className="form-two">
          <div className="field"><label>Toss won by</label><select value={setup.toss_winner} onChange={(e) => setSetup({ ...setup, toss_winner: e.target.value })} data-testid="setup-toss"><option value="">—</option><option value={match.team_a}>{match.team_a}</option><option value={match.team_b}>{match.team_b}</option></select></div>
          <div className="field"><label>Elected to</label><select value={setup.toss_decision} onChange={(e) => setSetup({ ...setup, toss_decision: e.target.value })} data-testid="setup-decision"><option value="bat">Bat</option><option value="bowl">Bowl</option></select></div>
        </div>
        {[["a", match.team_a], ["b", match.team_b]].map(([side, team]) => <div key={side} className="squad-setup">
          <p className="squad-setup-team">{team}</p>
          {teamSquads[side].length > 0 && <div className="squad-pick" data-testid={`squad-pick-${side}`}>
            <small>TAP TO ADD FROM SQUAD</small>
            <div className="squad-chips">{teamSquads[side].map((n) => {
              const inXi = setup[`squad_${side}_xi`].split("\n").map((x) => x.trim()).includes(n);
              return <button type="button" key={n} className={`squad-chip ${inXi ? "picked" : ""}`} onClick={() => {
                if (inXi) return;
                const current = setup[`squad_${side}_xi`].split("\n").map((x) => x.trim()).filter(Boolean);
                if (current.length >= 11) return;
                setSetup({ ...setup, [`squad_${side}_xi`]: [...current, n].join("\n") });
              }} data-testid={`pick-${side}-${n.replace(/\s+/g, "-")}`}>{n}</button>;
            })}</div>
          </div>}
          <div className="field"><label>Playing XI (one name per line)</label><textarea rows={6} value={setup[`squad_${side}_xi`]} onChange={(e) => setSetup({ ...setup, [`squad_${side}_xi`]: e.target.value })} data-testid={`setup-squad-${side}`} /></div>
          <div className="form-two">
            <div className="field"><label>Impact players</label><textarea rows={3} value={setup[`squad_${side}_impact`]} onChange={(e) => setSetup({ ...setup, [`squad_${side}_impact`]: e.target.value })} data-testid={`setup-impact-${side}`} /></div>
            <div className="field"><label>Bench</label><textarea rows={3} value={setup[`squad_${side}_bench`]} onChange={(e) => setSetup({ ...setup, [`squad_${side}_bench`]: e.target.value })} data-testid={`setup-bench-${side}`} /></div>
          </div>
        </div>)}
        <button className="button button-red scorer-btn" onClick={saveSetup} disabled={busy} data-testid="setup-save">Save setup</button>
      </div> : !playing ? <div className="scorer-panel" data-testid="scorer-start">
        <h2>{inningsIdx === 0 ? "Start innings 1" : `Start innings 2 — ${compInn?.team} need ${comp?.target} to win`}</h2>
        <div className="field"><label>Striker</label><select value={startInn.striker} onChange={(e) => setStartInn({ ...startInn, striker: e.target.value })} data-testid="start-striker"><option value="">—</option>{battingSquad.map((n) => <option key={n}>{n}</option>)}</select></div>
        <div className="field"><label>Non-striker</label><select value={startInn.non_striker} onChange={(e) => setStartInn({ ...startInn, non_striker: e.target.value })} data-testid="start-nonstriker"><option value="">—</option>{battingSquad.map((n) => <option key={n}>{n}</option>)}</select></div>
        <div className="field"><label>Opening bowler</label><select value={startInn.bowler} onChange={(e) => setStartInn({ ...startInn, bowler: e.target.value })} data-testid="start-bowler"><option value="">—</option>{bowlingSquad.map((n) => <option key={n}>{n}</option>)}</select></div>
        <button className="button button-red scorer-btn" onClick={startInnings} disabled={busy || !startInn.striker || !startInn.non_striker || !startInn.bowler} data-testid="start-go">Start play</button>
      </div> : <div className="scorer-panel" data-testid="scorer-pad">
        <div className="scorer-now">
          <div><small>STRIKER</small><b>{playing.striker}</b><span>{compInn?.striker_stats ? `${compInn.striker_stats.runs} (${compInn.striker_stats.balls})` : "0 (0)"}</span></div>
          <div><small>NON-STRIKER</small><b>{playing.non_striker}</b></div>
          <div><small>BOWLER</small><b>{playing.bowler || "—"}</b><span>{compInn?.bowler_stats ? `${compInn.bowler_stats.overs} ov · ${compInn.bowler_stats.runs} runs · ${compInn.bowler_stats.wickets} wkts` : ""}</span></div>
          <div><small>THIS OVER</small><div className="ball-chips">{(compInn?.recent || []).map((b, i) => <i key={i} className={b === "W" ? "chip w" : b === "4" || b === "6" ? "chip b" : "chip"}>{b}</i>)}</div></div>
        </div>

        {!playing.bowler && <div className="scorer-bowler-pick" data-testid="bowler-pick">
          <div className="field"><label>Over complete — choose next bowler</label><select value={bowlerPick} onChange={(e) => setBowlerPick(e.target.value)} data-testid="bowler-select"><option value="">—</option>{bowlingSquad.filter((n) => n !== compInn?.bowler).map((n) => <option key={n}>{n}</option>)}</select></div>
          <button className="button button-red scorer-btn" onClick={setBowler} disabled={busy || !bowlerPick} data-testid="bowler-set">Set bowler</button>
        </div>}

        {extraMode && <p className="scorer-mode" data-testid="extra-mode">{extraMode === "noball" ? "NO BALL — tap runs off the bat" : extraMode === "wide" ? "WIDE — tap additional wides (0 = just the wide)" : `${extraMode === "bye" ? "BYE" : "LEG BYE"} — tap runs taken`} <button onClick={() => setExtraMode("")} data-testid="extra-cancel">cancel</button></p>}

        <div className="run-grid" data-testid="run-grid">
          {[0, 1, 2, 3, 4, 5, 6].map((n) => <button key={n} className={`run-btn ${n === 4 || n === 6 ? "boundary" : ""}`} disabled={busy || !playing.bowler} onClick={() => {
            if (extraMode === "wide") recordBall(0, "wide", n);
            else if (extraMode === "noball") recordBall(n, "noball", 0);
            else if (extraMode === "bye" || extraMode === "legbye") recordBall(0, extraMode, n);
            else recordBall(n);
          }} data-testid={`run-${n}`}>{n}</button>)}
        </div>

        <div className="extra-grid">
          {[["wide", "Wide"], ["noball", "No Ball"], ["bye", "Bye"], ["legbye", "Leg Bye"]].map(([key, label]) => <button key={key} className={`extra-btn ${extraMode === key ? "on" : ""}`} disabled={busy || !playing.bowler} onClick={() => setExtraMode(extraMode === key ? "" : key)} data-testid={`extra-${key}`}>{label}</button>)}
        </div>

        <div className="wicket-zone">
          <button className={`wicket-btn ${wicketMode ? "on" : ""}`} disabled={busy || !playing.bowler} onClick={() => setWicketMode(!wicketMode)} data-testid="wicket-toggle">WICKET</button>
          {wicketMode && <div className="wicket-panel" data-testid="wicket-panel">
            <div className="form-two">
              <div className="field"><label>Dismissal</label><select value={wicket.type} onChange={(e) => setWicket({ ...wicket, type: e.target.value })} data-testid="wicket-type">{WICKET_TYPES.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></div>
              <div className="field"><label>Player out</label><select value={wicket.player_out} onChange={(e) => setWicket({ ...wicket, player_out: e.target.value })} data-testid="wicket-out"><option value="">{playing.striker} (striker)</option><option value={playing.non_striker}>{playing.non_striker}</option></select></div>
            </div>
            <div className="field"><label>Fielder (for caught / run out / stumped)</label><input type="text" value={wicket.fielder} onChange={(e) => setWicket({ ...wicket, fielder: e.target.value })} data-testid="wicket-fielder" /></div>
            <p className="scorer-mode">Now tap the runs completed on this ball (usually 0) to record the wicket.</p>
          </div>}
        </div>

        <div className="field"><label>Optional note (shot / description)</label><input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. pulled over midwicket" data-testid="ball-note" /></div>

        <div className="scorer-actions">
          <button className="button button-outline" onClick={undo} disabled={busy} data-testid="scorer-undo"><Undo2 size={15} /> Undo last ball</button>
          <button className="button button-outline" onClick={() => { setExtraMode(""); setWicketMode(false); }} data-testid="scorer-reset"><RotateCcw size={15} /> Reset pad</button>
        </div>
      </div>}
    </>}
    {error && <div className="auth-error scorer-error" data-testid="scorer-error">{error}</div>}
  </div>;
}
